import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import './MoveSamplesModal.css';

import { useSample } from '../../../hook/useSample';
import { useStorage } from '../../../hook/useStorage';
import ModalBase from '../ModalBase';
import { sortType } from '../../SamplesList/SampleListContent';
import SamplesList, { viewModeList as sampleView } from '../../SamplesList/SamplesList';
import SimpleButton, { ButtonTypes } from '../../SimpleComponents/SimpleButton';

const moveModalStyles = {
	content: {
		width: '600px',
		maxHeight: '99%',
		maxWidth: '100%',
	},
};

/**
 * Modal dialog for samples move
 * @component
 */
const MoveSamplesModal = ({ isOpen, default_target, defaultYes, samples, onConfirm, onCancel }) => {
	const { storages } = useStorage();
	const { moveSamples } = useSample();

	const [targetStorageId, setTargetStorageId] = useState(() => {
		// Prepare initial target storage
		if (default_target) return default_target.id;
		else if (storages.length) return storages[Object.keys(storages)[0]].id;
		return 0;
	});

	const [selectedIds, setSelectedIds] = useState(() => {
		// Prepare selected samples Set
		let selected = new Set();
		for (let sample_id in samples) {
			selected.add(samples[sample_id].id);
		}
		return selected;
	});

	useEffect(() => {
		// Clearing the selected items if samples have changed
		let selected = new Set();
		for (let sample_id in samples) {
			selected.add(samples[sample_id].id);
		}
		setSelectedIds(selected);
	}, [samples]);

	const sortByName = (k1, k2) => {
		return storages[k1].name > storages[k2].name ? 1 : -1;
	};

	return (
		<ModalBase className="modal-move-samples" isOpen={isOpen} style={moveModalStyles} onRequestClose={onCancel}>
			<div className="modal-header">
				<div className="modal-title">Move samples</div>
				<div className="modal-close-x" onClick={onCancel} />
			</div>
			<div className="modal-content">
				<div className="panel">
					<SamplesList
						active_sample={null}
						default_sort={sortType.collectorNumber}
						isOwned={true}
						samples={samples}
						selectedIds={selectedIds}
						show_change_mode={false}
						show_checkbox={true}
						show_subhead={true}
						showStorage={false}
						updated_sample={null}
						viewMode={sampleView.rows}
						onActivate={() => {}}
						onSelect={handleSelect}
					/>
				</div>
			</div>
			<div className="modal-control">
				<div className="target">
					<div className="label">Move to: </div>
					<select value={targetStorageId} onChange={handleChangeTarget}>
						{Object.keys(storages)
							.sort((k1, k2) => sortByName(k1, k2))
							.map((key) => (
								<option key={key} value={storages[key].id}>
									{storages[key].name}
								</option>
							))}
					</select>
				</div>
				<div className="spacer" />
				<div className="buttons-wrapper">
					<SimpleButton
						autoFocus={defaultYes ? true : false}
						title="Confirm"
						type={ButtonTypes.DEFAULT}
						value="Confirm"
						onClick={handleConfirm}
					/>
					<SimpleButton
						autoFocus={defaultYes ? false : true}
						title="Cancel"
						type={ButtonTypes.GREY}
						value="Cancel"
						onClick={onCancel}
					/>
				</div>
			</div>
		</ModalBase>
	);

	function handleSelect(sample, select_all = false, select_none = false) {
		if (select_all) {
			let selected_upd = new Set();
			for (let sample_id in samples) {
				selected_upd.add(samples[sample_id].id);
			}
			setSelectedIds(selected_upd);
			return;
		}
		if (select_none) {
			setSelectedIds(new Set());
			return;
		}
		// Prepare new selected state
		let selected_upd = new Set(selectedIds);
		if (selected_upd.has(sample.id)) selected_upd.delete(sample.id);
		else selected_upd.add(sample.id);
		// Update state
		setSelectedIds(selected_upd);
	}

	function handleChangeTarget(event) {
		setTargetStorageId(event.target.value);
	}

	function handleConfirm() {
		if (targetStorageId && selectedIds.size) {
			moveSamples(Array.from(selectedIds), targetStorageId, () => {
				onConfirm?.();
			});
		} else {
			console.warn('Select samples to move');
		}
	}
};

MoveSamplesModal.propTypes = {
	// Open state of modal
	isOpen: PropTypes.bool.isRequired,
	// Default target storage object
	default_target: PropTypes.object,
	// Default "Yes" button focus when the modal is opened
	defaultYes: PropTypes.bool,
	// Associative array (object) of samples for moving
	samples: PropTypes.objectOf(PropTypes.object).isRequired,
	// Callback function for submit move
	onConfirm: PropTypes.func,
	// Callback function for cancel move
	onCancel: PropTypes.func,
};

export default MoveSamplesModal;
